/* Eén definitie van «wat voor oppervlak is dit», gedeeld door JS én CSS.

   Hiervoor stonden er twee: JavaScript sloot touchapparaten uit van de
   splitview, maar de CSS keek alleen naar afmetingen. Een iPad in landscape
   kreeg daardoor van JS een fullscreen kamer en van CSS een rechterpaneel van
   560 px — met een leeg linkervlak als resultaat. Twee poorten die uit elkaar
   lopen, lopen vroeg of laat uit elkaar.

   De oplossing is niet «dezelfde regel twee keer opschrijven», maar één bron:
   dit bepaalt het oppervlak en schrijft het als `data-surface` op <html>. Alle
   CSS hangt daaraan. Vóórdat dit script draait staat er niets, en dan geldt de
   veilige mobiele stand — precies wat je wilt.

   Touch wint altijd van formaat. Een iPad met trackpad meldt `any-pointer:
   fine` én `any-hover: hover`, maar houdt zijn touchscherm en zijn harde
   WebGL-limiet; `any-pointer: coarse` blijft daar waar. */
import { useEffect, useState } from "react";

export const DESKTOP_MIN_W = 1050;
export const DESKTOP_MIN_H = 650;

/** Queries die samen «dit apparaat kan aangeraakt worden» betekenen. */
export const TOUCH_QUERIES = ["(any-pointer: coarse)", "(any-hover: none)"] as const;
export const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

export type Surface = "desktop" | "touch";

/** Bepaal het oppervlak zonder React — dezelfde regel, één implementatie. */
export function currentSurface(width = window.innerWidth,
                               height = window.innerHeight): Surface {
  return !anyMatches(TOUCH_QUERIES) &&
    width >= DESKTOP_MIN_W && height >= DESKTOP_MIN_H ? "desktop" : "touch";
}

/** Zet `data-surface` vóór de eerste paint.
 *
 *  Wordt aangeroepen vanuit main.tsx, bovenaan, dus vóór React ook maar iets
 *  rendert. Zonder deze stap tekent de browser één frame met de mobiele
 *  standaard voordat de eerste effect-ronde het attribuut zet — op desktop een
 *  zichtbare sprong. */
export function stampSurface(): void {
  document.documentElement.dataset.surface = currentSurface();
}

const anyMatches = (queries: readonly string[]): boolean =>
  queries.some(q => matchMedia(q).matches);

/** Waar of één van de queries matcht.
 *
 *  Bewust één hook voor de héle lijst. `useMediaFlag(a) || useMediaFlag(b)`
 *  leek hetzelfde te doen, maar `||` kortsluit: op een touchapparaat werd de
 *  tweede hook nooit aangeroepen. Verandert de pointerconfiguratie, dan
 *  verandert het aantal hooks tussen twee renders en crasht React. */
export function useAnyMedia(queries: readonly string[]): boolean {
  const [on, setOn] = useState(() => anyMatches(queries));
  const key = queries.join("|");
  useEffect(() => {
    const list = key.split("|");
    const mqs = list.map(q => matchMedia(q));
    const sync = () => setOn(mqs.some(mq => mq.matches));
    sync();
    for (const mq of mqs) mq.addEventListener("change", sync);
    return () => { for (const mq of mqs) mq.removeEventListener("change", sync); };
  }, [key]);
  return on;
}

/** Eén enkele mediaquery — dezelfde vaste hookvorm. */
export const useMedia = (query: string): boolean => useAnyMedia([query]);

/** Het oppervlak, live gevolgd, en meteen als `data-surface` op <html>. */
export function useSurface(width: number, height: number): Surface {
  const touch = useAnyMedia(TOUCH_QUERIES);
  const surface: Surface =
    !touch && width >= DESKTOP_MIN_W && height >= DESKTOP_MIN_H
      ? "desktop" : "touch";
  useEffect(() => {
    document.documentElement.dataset.surface = surface;
    return () => { delete document.documentElement.dataset.surface; };
  }, [surface]);
  return surface;
}
